import { useQuery } from "@tanstack/react-query";
import type { FilterTree } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { encodeFilterTreeParam, hashFilterTree } from "./filter-node-mapping";
import type { AdminRequestQueueRow } from "./types";

export type UseMdfRequestsOptions = {
  /** Status filter — `"all"` (or `null`) returns every status. */
  status?: string | null;
  /** Optional fund scope. Set when the same hook backs a fund-detail tab. */
  fundId?: string | null;
  /**
   * Optional FilterTree from a Saved View or ad-hoc URL filter. Composes
   * AND with the chip filters above. Sub-bucket 17 — first Saved Views
   * consumer.
   */
  filterNode?: FilterTree | null;
};

// GET /api/admin/mdf/requests?status=&fundId=&filter= → { requests: AdminRequestQueueRow[] }
//
// Sub-bucket 15 shipped the cross-fund queue with status + fundId chip
// filters; sub-bucket 17 extends it with the FilterTree branch driven by
// the active Saved View (or an ad-hoc AddFilterMenu URL filter). The hash
// of the filter is appended to the query key so distinct filter trees
// cache independently; mutation hooks invalidate the bare
// `queryKeys.mdf.requests()` (TanStack prefix matching sweeps every
// filter cache).
export function useMdfRequests(options: UseMdfRequestsOptions = {}) {
  const status = options.status ?? null;
  const fundId = options.fundId ?? null;
  const filterNode = options.filterNode ?? null;
  const filterHash = hashFilterTree(filterNode);
  return useQuery<AdminRequestQueueRow[], ApiClientError>({
    queryKey: [...queryKeys.mdf.requests(), status, fundId, filterHash],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (fundId) params.set("fundId", fundId);
      const encoded = encodeFilterTreeParam(filterNode);
      if (encoded) params.set("filter", encoded);
      const qs = params.toString();
      const body = await apiFetch<{ requests: AdminRequestQueueRow[] }>(
        `/admin/mdf/requests${qs ? `?${qs}` : ""}`,
      );
      return body.requests;
    },
  });
}
